// components/MapWindow.tsx — pop-out map view for second-monitor use.
//
// Mounted by main.tsx when the URL carries `?map=<storyId>`. The popped-out
// window is the SAME origin as the main app, so:
//   - Auth tokens in localStorage are visible here (auth/tokens.ts reads
//     them just like in the main app).
//   - The BroadcastChannel API works between this window and the main one
//     because both share the origin.
//
// Lifecycle:
//   1. Mount → fetch /api/stories/:id once via authedFetch. That gives us
//      the initial state + scenarioId; render FullMap (in embedded mode,
//      no modal scrim).
//   2. Subscribe to BroadcastChannel `ember-map-<storyId>`. The main window
//      pushes a `{ type: "state", state }` message on every turn. On
//      receive, swap our local state — the map re-renders.
//   3. On subscribe, also POST a `{ type: "request_state" }` so the main
//      window pushes its current state right away — covers the case where
//      the pop-out was opened mid-game (the server's saved blob may be
//      one turn behind the live in-memory state).
//
// Auth failure: a 401 from /api/stories means the user isn't signed in on
// this origin. We don't try to push them to the login screen here — the
// pop-out window is small and the login UX wasn't designed for it. Instead
// we render a one-line "Sign in on the main window" message; the user can
// close the pop-out themselves.
//
// Engine purity: this file imports `migrateState` from engine.ts but does
// not mutate engine state. The migration is a pure transform of the loaded
// JSON blob — same call the main App makes after `getStory`. No I/O leaks
// into engine.ts.

import {
  useEffect,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import { migrateState, type GameState } from "../engine";
import { getStory, StoriesApiError } from "../services/storiesApi";
import ScenarioThemeWrapper from "../scenarios/ScenarioThemeWrapper";
import { FullMap } from "./Map";

/**
 * Message envelope on the BroadcastChannel. Kept small + flat so missing
 * fields fail closed (we treat anything not matching `type: "state"` as
 * a no-op).
 *
 * Mirrored in App.tsx — keep the shapes in sync.
 */
type MapBroadcastMessage =
  | { type: "state"; state: GameState }
  | { type: "request_state" };

export default function MapWindow({ storyId }: { storyId: string }) {
  const { t } = useTranslation("game");

  const [state, setState] = useState<GameState | null>(null);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authBlocked, setAuthBlocked] = useState(false);

  // Initial hydrate: load the story state from the server. Uses authedFetch
  // under the hood (via getStory) so the access token from localStorage
  // travels with the request. A 401 / 403 lands us in the auth-blocked
  // branch with a friendly message.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { story } = await getStory(storyId);
        if (cancelled) return;
        const migrated = migrateState(story.state, story.scenarioId);
        setState(migrated);
        setScenarioId(story.scenarioId);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof StoriesApiError && (err.status === 401 || err.status === 403)) {
          setAuthBlocked(true);
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load map.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storyId]);

  // BroadcastChannel — listen for state updates from the main window. Each
  // pop-out picks the channel name off its storyId so multiple pop-outs
  // (one per story) don't cross-talk.
  useEffect(() => {
    // Older browsers / private modes may not expose BroadcastChannel. Fail
    // soft — the initial fetch still works, the user just won't see live
    // updates after subsequent turns.
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(`ember-map-${storyId}`);
    channel.onmessage = (e: MessageEvent<MapBroadcastMessage>) => {
      const msg = e.data;
      if (msg?.type === "state" && msg.state) {
        setState(msg.state);
      }
    };
    // Request an immediate state push from the main window — covers the
    // case where the user opened the pop-out while paused on a state
    // that's newer than the server's last-saved blob. Failure is silent;
    // the initial /api/stories fetch already gave us a baseline.
    channel.postMessage({ type: "request_state" } satisfies MapBroadcastMessage);
    return () => {
      channel.close();
    };
  }, [storyId]);

  if (authBlocked) {
    return (
      <div style={messageWrap}>
        <div style={messageBody}>{t("map.windowOnly_signinNeeded")}</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={messageWrap}>
        <div style={messageBody}>{error}</div>
      </div>
    );
  }
  if (!state || !scenarioId) {
    return (
      <div style={messageWrap}>
        <div style={messageBody}>{t("map.windowLoading")}</div>
      </div>
    );
  }

  // Render FullMap in embedded mode (no scrim, full-window layout). The
  // close button calls window.close() — the OS-level X yields the same
  // outcome, the main window's pop-out poller will detect either.
  return (
    <ScenarioThemeWrapper
      scenarioId={scenarioId}
      currentDimension={state.dimension ?? 0}
    >
      <FullMap
        state={state}
        scenarioId={scenarioId}
        npcs={state.npcs}
        embedded
        onClose={() => {
          try {
            window.close();
          } catch {
            // ignore — some browsers refuse window.close() on pages that
            // weren't opened by script. The user can use the OS X.
          }
        }}
      />
    </ScenarioThemeWrapper>
  );
}

// ── Inline styles ────────────────────────────────────────────────────────

// Fills the window with the scenario theme so any pre-load / error /
// auth-blocked message appears against the page palette, not a white
// background.
const messageWrap: CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg-gradient, #1a1410)",
  color: "var(--ink, #e8d9b8)",
  fontFamily: "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
  padding: 24,
  textAlign: "center",
};

const messageBody: CSSProperties = {
  fontSize: "1rem",
  letterSpacing: "0.04em",
  maxWidth: 480,
};
